import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCloudUpload, IconFileText } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { updateArticle, uploadArticle } from '../services/api';

const ACCEPT = '.html,.htm,.zip';
const MAX_BYTES = 5 * 1024 * 1024;

function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function extractTitleFromHtml(html) {
  const match = /<title[^>]*>([^<]*)<\/title>/i.exec(html || '');
  return match ? match[1].trim() : '';
}

export default function AviaryV2ArticleNewUploadPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const back = location.state?.back || { path: '/v2/articles/new', label: 'Add' };
  const defaultRole = location.state?.defaultRole || 'promo';
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const previewUrl = useMemo(() => {
    const s = slug || slugify(title) || 'your-article';
    return `aviary.co/a/${s}`;
  }, [slug, title]);

  const acceptFile = useCallback(async (picked) => {
    if (!picked) return;
    if (picked.size > MAX_BYTES) {
      notifications.show({
        title: 'File too large',
        message: 'Articles must be <= 5 MB. Try compressing images or splitting the piece.',
        color: 'red',
      });
      return;
    }
    setFile(picked);
    let detected = '';
    if (/\.(html?|htm)$/i.test(picked.name)) {
      try {
        const text = await picked.text();
        detected = extractTitleFromHtml(text);
      } catch {
        // Fall through to filename.
      }
    }
    if (!detected) {
      detected = picked.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
    }
    setTitle(detected);
    if (!slugDirty) setSlug(slugify(detected));
  }, [slugDirty]);

  function handleFileChange(e) {
    const picked = e.target.files?.[0];
    if (picked) acceptFile(picked);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const picked = e.dataTransfer?.files?.[0];
    if (picked) acceptFile(picked);
  }

  function handleTitleChange(e) {
    const next = e.target.value;
    setTitle(next);
    if (!slugDirty) setSlug(slugify(next));
  }

  function handleSlugChange(e) {
    setSlug(slugify(e.target.value));
    setSlugDirty(true);
  }

  async function save({ publish }) {
    if (!file || !title.trim()) return;
    setUploading(true);
    try {
      const saved = await uploadArticle(api, file, {
        slug: slug || undefined,
        title: title.trim() || undefined,
        articleStatus: publish ? 'published' : 'draft',
        originNote: 'Uploaded HTML',
      });
      notifications.show({
        title: publish ? 'Article published' : 'Draft saved',
        message: `"${saved.title || title}" is ${publish ? 'live' : 'saved as a draft'}.`,
        color: 'sage',
      });
      const savedSlug = String(saved?.slug || '').trim();
      if (defaultRole === 'context' && savedSlug) {
        await updateArticle(api, savedSlug, { articleRole: 'context' });
      }
      const nextPath = defaultRole === 'context' ? '/v2/knowledge-base' : '/v2/articles';
      navigate(nextPath, { state: { defaultRole } });
    } catch (err) {
      notifications.show({
        title: 'Upload failed',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  }

  const canSave = Boolean(file && title.trim());

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 720, margin: '0 auto' }}>
      <Stack gap="lg">
        <UnstyledButton
          onClick={() => navigate(back.path)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--aviary-muted)',
            fontSize: 13,
            width: 'fit-content',
          }}
        >
          <IconArrowLeft size={14} /> Back to {back.label}
        </UnstyledButton>

        <Stack gap={4}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Upload an HTML file
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            Already wrote it elsewhere? Drop the file and we host it.
          </Text>
        </Stack>

        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <UnstyledButton
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                display: 'block',
                width: '100%',
                padding: 28,
                borderRadius: 12,
                border: `2px dashed ${dragOver ? 'var(--mantine-color-sage-6)' : 'var(--aviary-disabled)'}`,
                background: dragOver ? 'var(--mantine-color-sage-0, #eef4f0)' : 'var(--aviary-surface)',
                textAlign: 'center',
                transition: 'border-color 120ms ease, background 120ms ease',
              }}
            >
              <Stack gap={6} align="center">
                <IconCloudUpload size={28} style={{ color: 'var(--aviary-muted)' }} />
                {file ? (
                  <>
                    <Text fw={600} style={{ color: 'var(--aviary-ink)' }}>{file.name}</Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                      {(file.size / 1024).toFixed(0)} KB - click to replace
                    </Text>
                  </>
                ) : (
                  <>
                    <Text style={{ color: 'var(--aviary-ink)' }}>
                      Drop a file here, or <strong>click to browse</strong>
                    </Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                      .html / .htm / .zip - up to 5 MB - self-contained
                    </Text>
                  </>
                )}
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </UnstyledButton>

            <TextInput
              label="Title"
              placeholder="Auto-filled from the file"
              value={title}
              onChange={handleTitleChange}
              disabled={!file}
            />

            <TextInput
              label="Slug"
              description={previewUrl}
              value={slug}
              onChange={handleSlugChange}
              disabled={!file}
            />

            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => save({ publish: false })}
                disabled={!canSave || uploading}
              >
                Save as draft
              </Button>
              <Button
                color="sage"
                onClick={() => save({ publish: true })}
                disabled={!canSave || uploading}
                leftSection={uploading ? <Loader size={14} color="white" /> : null}
              >
                Publish
              </Button>
            </Group>

            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
              After publishing you can promote the article to specific segments of your base.
            </Text>
          </Stack>
        </Paper>

        <Paper p="sm" radius="md" style={{ background: 'var(--mantine-color-sage-0)', border: '1px dashed var(--mantine-color-sage-4)' }}>
          <Group gap={6} align="center">
            <IconFileText size={16} style={{ color: 'var(--mantine-color-sage-7)' }} />
            <Text size="xs" style={{ color: 'var(--mantine-color-sage-8)', fontStyle: 'italic' }}>
              Upload flow unchanged - this screen is dedicated to file-based articles.
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Box>
  );
}
